import { useState, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import {
  createRole,
  updateRole,
  getRole,
  type PermissionSummary,
} from "../../../api/accessManagement/rolesService";
import type { RoleFormData } from "./CreateRoleDrawer";
import type { RoleDrawerMode } from "./CreateRoleDrawer";

interface UseCreateRoleDrawerParams {
  show: boolean;
  onHide: () => void;
  mode: RoleDrawerMode;
  initialData?: RoleFormData;
  onSubmitSuccess?: () => void;
}

export function useCreateRoleDrawer({
  show,
  onHide,
  mode,
  initialData,
  onSubmitSuccess,
}: UseCreateRoleDrawerParams) {
  const { user } = useSelector((state: any) => state.Login);
  const isEdit = mode === "edit" && initialData;
  const isView = mode === "view" && initialData;

  const [roleCode, setRoleCode] = useState("");
  const [scopeType, setScopeType] = useState("");
  const [description, setDescription] = useState("");
  const [selectedPermissions, setSelectedPermissions] = useState<Set<string>>(
    new Set()
  );
  const [permissionSearch, setPermissionSearch] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<{
    roleCode?: string;
  }>({});
  const [isLoadingRole, setIsLoadingRole] = useState(false);
  const [rolePermissions, setRolePermissions] = useState<PermissionSummary[]>([]);

  useEffect(() => {
    if (show) {
      setSubmitError(null);
      setFieldErrors({});
      if ((isEdit || isView) && initialData) {
        // Start with existing list data
        setRoleCode(initialData.code);
        setScopeType(initialData.scopeType ?? "");
        setDescription(initialData.description ?? "");
        setSelectedPermissions(new Set(initialData.permissionCodes));
        setRolePermissions([]);

        // Fetch fresh details from RBAC roles/{code} for full permissions & scope
        setIsLoadingRole(true);
        getRole(initialData.code)
          .then((role) => {
            if (role) {
              if (role.allowedScopeTypes && role.allowedScopeTypes.length > 0) {
                setScopeType(role.allowedScopeTypes[0] ?? "");
              }
              if (role.name && !initialData.description) {
                setDescription(role.name);
              }
              const permissionCodes =
                role.permissionCodes && role.permissionCodes.length > 0
                  ? role.permissionCodes
                  : Array.isArray(role.permissions)
                    ? role.permissions.map((p) => p.code)
                    : [];
              if (permissionCodes.length > 0) {
                setSelectedPermissions(new Set(permissionCodes));
              }
              if (Array.isArray(role.permissions)) {
                setRolePermissions(role.permissions);
              } else {
                setRolePermissions([]);
              }
            }
          })
          .catch((err) => {
            // eslint-disable-next-line no-console
            console.error("Failed to load role details:", err);
          })
          .finally(() => {
            setIsLoadingRole(false);
          });
      } else {
        setRoleCode("");
        setScopeType("");
        setDescription("");
        setSelectedPermissions(new Set());
        setRolePermissions([]);
      }
      setPermissionSearch("");
    }
  }, [show, isEdit, isView, initialData]);

  const handleRoleCodeChange = useCallback((value: string) => setRoleCode(value), []);
  const handleScopeTypeChange = useCallback((value: string) => setScopeType(value), []);
  const handleDescriptionChange = useCallback((value: string) => setDescription(value), []);
  const handlePermissionSearchChange = useCallback(
    (value: string) => setPermissionSearch(value),
    []
  );

  const handlePermissionToggle = useCallback((code: string) => {
    setSelectedPermissions((prev) => {
      const next = new Set(prev);
      if (next.has(code)) {
        next.delete(code);
      } else {
        next.add(code);
      }
      return next;
    });
  }, []);

  const handleSubmit = useCallback(async () => {
    setSubmitError(null);
    setFieldErrors({});
    const trimmedCode = roleCode.trim();
    const trimmedDescription = description.trim();

    const newFieldErrors: { roleCode?: string } = {};
    if (!trimmedCode) {
      newFieldErrors.roleCode = "Role code is required";
    }
    if (Object.keys(newFieldErrors).length > 0) {
      setFieldErrors(newFieldErrors);
      return;
    }

    const permissions = Array.from(selectedPermissions);
    const name = trimmedDescription || trimmedCode;

    setIsSubmitting(true);
    try {
      if (isEdit && initialData) {
        // Update existing role by code (path param)
        const payload: any = {
          name,
          permissions,
        };
        if (trimmedDescription) {
          payload.description = trimmedDescription;
        }
        if (scopeType) {
          payload.allowed_scope_types = [scopeType];
        }
        await updateRole(initialData.code, payload);
      } else {
        // Create new role
        const createdBy =
          user?.id || user?._id || (user as any)?.user_id || (user as any)?.userId || "";

        const payload: any = {
          code: trimmedCode,
          name,
          is_system: false,
          created_by: createdBy,
          permissions,
        };
        if (trimmedDescription) {
          payload.description = trimmedDescription;
        }
        if (scopeType) {
          payload.allowed_scope_types = [scopeType];
        }

        await createRole(payload);
      }
      if (onSubmitSuccess) {
        onSubmitSuccess();
      } else {
        onHide();
      }
    } catch (err) {
      setSubmitError(
        err instanceof Error
          ? err.message
          : isEdit
          ? "Failed to update role"
          : "Failed to create role"
      );
    } finally {
      setIsSubmitting(false);
    }
  }, [
    isEdit,
    initialData,
    roleCode,
    scopeType,
    description,
    selectedPermissions,
    onHide,
    onSubmitSuccess,
    user,
  ]);

  const drawerTitle =
    isView ? "View Role" : isEdit ? "Edit Role" : "Create Role";

  return {
    functions: {
      handleRoleCodeChange,
      handleScopeTypeChange,
      handleDescriptionChange,
      handlePermissionSearchChange,
      handlePermissionToggle,
      handleSubmit,
    },
    values: {
      roleCode,
      scopeType,
      description,
      selectedPermissions,
      permissionSearch,
      isEdit,
      isView,
      drawerTitle,
      submitError,
      isLoadingRole,
      fieldErrors,
      rolePermissions,
    },
    loading: {
      isSubmitting,
    },
  };
}
